import { FC } from 'react'
import { TableRow } from '@mui/material'
import { toast } from 'react-toastify'

import { StyledTableCell } from 'Components/Table/StyledTableCell'
import { CountryFlagIcon } from 'Components/Icons/CountryFlagIcon'
import { CurrencyText } from 'Components/Text/CurrencyText'
import { Chip } from 'Components/Chip/Chip'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { InvoiceStatus } from 'Pages/Employer/BillingPage/InvoiceTable/InvoiceStatus'
import { PayButton } from 'Pages/Employer/BillingPage/PayInvoice/PayButton'
import { InvoiceActionsTableCell } from 'Pages/Employer/BillingPage/InvoiceTable/InvoiceActionsTableCell'
import { HoverIconCard } from 'Components/HoverIconCard'
import { AppIconMap, AppIcons } from 'Utils/types/appIcons'

import { useCountries } from 'Hooks/useCountries'
import { getStripeInvoiceUrl } from 'Services/invoices/invoiceService'
import { CountryCode } from 'Utils/types/countries'

import { AppNotification } from 'Utils/types/appNotification'
import { PurpleText } from 'Utils/styles/text'
import { InvoiceState, EmployerAdminInvoiceTableRow, InvoiceType } from 'Utils/types/invoice'
import { displayMonthAndYear } from 'Utils/helpers/dateUtils'
import { elfGreenFaded, elfGreen, electricViolet, electricVioletFaded } from 'Utils/styles/colors'
import { getCurrency } from 'Utils/types/currencies'
import { FlexRow, spacingSM } from 'Utils/styles/spacing'

type InvoiceRowProps = {
  row: EmployerAdminInvoiceTableRow
  setSelected: () => void
  toggleModal: () => void
  showType: boolean
  showReimbursementFee?: boolean
  isJoonAdmin?: boolean
}

export const InvoiceRow: FC<InvoiceRowProps> = ({
  row,
  setSelected,
  toggleModal,
  showType,
  showReimbursementFee = false,
  isJoonAdmin = false
}) => {
  const { showCountries } = useCountries(isJoonAdmin)

  const backgroundColor = row.type === InvoiceType.JOON ? elfGreenFaded : electricVioletFaded
  const fontColor = row.type === InvoiceType.JOON ? elfGreen : electricViolet
  const invoiceCurrency = row.invoice.currencyCode
  const benefitsCurrency = getCurrency(row.invoice.countryCode)

  const redirectToStripeInvoice = async () => {
    const { data: stripeInvoiceUrl } = await getStripeInvoiceUrl({
      employerId: row.employerId,
      invoiceId: row.id
    })
    if (stripeInvoiceUrl?.url) {
      window.open(stripeInvoiceUrl.url, '_blank')
    } else {
      toast.error(AppNotification.StripeInvoiceLinkFailure)
    }
  }

  return (
    <TableRow key={row.id}>
      <StyledTableCell>{displayMonthAndYear(row.date)}</StyledTableCell>
      {showCountries && (
        <StyledTableCell>
          <CountryFlagIcon text={row.countryCode} countryCode={row.countryCode} />
        </StyledTableCell>
      )}
      <StyledTableCell>
        <InvoiceStatus
          invoiceState={row.state}
          payDate={row.payDate}
          previousPaymentFailed={row.previousPaymentFailed}
        />
      </StyledTableCell>
      <StyledTableCell>
        {row.state === InvoiceState.Open ? (
          row.invoice.isInAutoPayment ? (
            'Scheduled'
          ) : (
            <FlexRow gap={spacingSM}>
              <PayButton invoice={row.invoice} />
              {row.stripeInvoiceId && (
                <StyledButton
                  primary
                  size="small"
                  id="stripe.pay.label"
                  onClick={redirectToStripeInvoice}
                />
              )}
            </FlexRow>
          )
        ) : (
          <FlexRow gap={spacingSM}>
            {row.paymentMethod}
            {row.invoice.stripeChargedAmount && row.countryCode !== CountryCode.US && (
              <HoverIconCard
                iconSrc={AppIconMap[AppIcons.InfoCircle]}
                altText="Stripe Charged Amount"
              >
                <span>{`Stripe charge amount: $${row.invoice.stripeChargedAmount}`}</span>
              </HoverIconCard>
            )}
          </FlexRow>
        )}
      </StyledTableCell>
      <StyledTableCell>
        <PurpleText>
          <CurrencyText value={row.joonFees} currencyCode={invoiceCurrency} />
        </PurpleText>
      </StyledTableCell>
      {showReimbursementFee && (
        <StyledTableCell>
          <PurpleText>
            <CurrencyText value={row.benefitTransferFees} currencyCode={row.invoice.currencyCode} />
          </PurpleText>
        </StyledTableCell>
      )}
      <StyledTableCell>
        <PurpleText>
          <CurrencyText value={row.benefitTransferAmount} currencyCode={benefitsCurrency} />
        </PurpleText>
      </StyledTableCell>
      {showType && (
        <StyledTableCell>
          <Chip text={row.type} backgroundColor={backgroundColor} fontColor={fontColor} />
        </StyledTableCell>
      )}
      <InvoiceActionsTableCell
        setSelected={setSelected}
        toggleModal={toggleModal}
        invoice={row.invoice}
      />
    </TableRow>
  )
}
